import { Matrix4, Vector3, type BatchedMesh, type Material } from 'three';
import type { CitizenAsset } from '../citizens/citizenAsset';
import { createCitizenBatch } from '../citizens/citizenBatch';
import { castTrades } from '../citizens/citizenCasting';
import { citizenGeometry } from '../citizens/citizenShapes';
import { createCitizenTier } from './citizenTier';
import { CROWD_TRADES } from './crowdConfig';
import type { CrowdFrame } from './crowdSim';
import type { Residents } from './residents';
import { createHeadings } from '../heading';

/**
 * How the crowd is drawn. A figure further than `farM` is not drawn: at that distance it is two
 * pixels of a frame a thousand high. Nor is the crowd drawn in the painter's view, which is the
 * painting's, and the painting shows the city without its people.
 *
 * Every figure drawn is the same citizen in the same pose at any distance; only how many
 * triangles it is made of changes. What a resident is seen doing from across a square is what
 * it is found doing from beside it.
 */
export const CROWD_DRAW = {
  farM: 520,
  /** Figures this near are drawn all round the camera; further ones only before it. */
  aroundM: 20,
  /** The cosine of the widest angle from the view's middle at which a figure is still drawn. */
  viewCos: Math.cos((75 * Math.PI) / 180),
  figures: 4000,
  /** A figure is drawn whole within the first distance, thinner to the second, thinnest beyond
   *  (tools/characters/bake_citizens.mjs). The distances are estimates of where the step shows. */
  formsM: [30, 90],
  /** No more than this many are drawn whole; the rest of those near take the next form. */
  wholeFigures: 160,
} as const;

/** The crowd's mesh, and the choice of whom it shows to an eye. */
export interface CrowdDrawing {
  readonly meshes: readonly BatchedMesh[];
  /** Draws those of a frame that an eye looking along `forward` can see; returns how many, and
   *  how many of them whole. */
  draw(
    out: CrowdFrame | null,
    eye: Vector3,
    forward: Vector3,
    sceneSeconds: number
  ): { readonly drawn: number; readonly near: number };
}

export function createCrowdDrawing(
  material: Material,
  citizens: CitizenAsset,
  residents: Residents
): CrowdDrawing {
  const forms = CROWD_DRAW.formsM.length + 1;
  for (const figure of citizens.figures)
    if (figure.lods.length !== forms)
      throw new Error(`citizens: ${figure.name} has ${String(figure.lods.length)} forms`);
  const batch = createCitizenBatch(
    material,
    citizens.figures.flatMap((figure) => figure.lods.map((lod) => citizenGeometry(citizens, lod))),
    CROWD_DRAW.figures,
    'crowd-citizens'
  );
  const tier = createCitizenTier(
    batch,
    citizens,
    castTrades(
      citizens,
      CROWD_TRADES.map((trade) => trade.trade)
    ),
    residents,
    forms
  );
  const matrix = new Matrix4(),
    scale = new Vector3();
  const headings = createHeadings(residents.count);
  const identities = new Int32Array(CROWD_DRAW.figures).fill(-1);
  batch.mesh.userData['crowdResidents'] = identities;

  return {
    meshes: [batch.mesh],
    draw(out, eye, forward, sceneSeconds) {
      headings.begin(sceneSeconds);
      batch.begin();
      let drawn = 0,
        whole = 0;
      for (let i = 0; i < (out?.count ?? 0) && drawn < CROWD_DRAW.figures; i++) {
        const [dx, dy, dz] = [out!.x[i]! - eye.x, out!.ground[i]! - eye.y, -out!.y[i]! - eye.z];
        const distance = Math.hypot(dx, dy, dz);
        if (distance > CROWD_DRAW.farM) continue;
        if (
          distance > CROWD_DRAW.aroundM &&
          dx * forward.x + dy * forward.y + dz * forward.z < distance * CROWD_DRAW.viewCos
        )
          continue;
        const r = out!.resident[i]!;
        identities[drawn] = r;
        matrix
          .makeRotationY(headings.at(r, out!.heading[i]!))
          .scale(scale.setScalar(residents.scale[r]!))
          // Layout y runs against the world's z.
          .setPosition(out!.x[i]!, out!.ground[i]!, -out!.y[i]!);
        let form = distance < CROWD_DRAW.formsM[0] ? 0 : distance < CROWD_DRAW.formsM[1] ? 1 : 2;
        if (form === 0 && whole >= CROWD_DRAW.wholeFigures) form = 1;
        tier.add(out!, i, matrix, form, sceneSeconds);
        if (form === 0) whole++;
        drawn++;
      }
      batch.end();
      return { drawn, near: whole };
    },
  };
}
